import { Cart, CartEvents, TypedCartEvent } from '../internals/core';
import { CartEventBus } from '../internals/event-bus';
import { MiniCart } from './mini-cart';
import { createLogger } from '@/lib/logger';

class MiniCartClearCartComponent extends HTMLElement {
  private abortController: AbortController | null = null;
  private eventBus: CartEventBus | null = null;
  private logger = createLogger('mini-cart-clear-cart');
  private clearCartButton: HTMLElement | null = null;
  private clearCartConfirmButton: HTMLElement | null = null;
  private clearCartConfirmCancel: HTMLElement | null = null;

  constructor() {
    super();
  }

  private openConfirm(): void {
    this.classList.add('is-open');
  }

  private closeConfirm(): void {
    this.classList.remove('is-open');
  }
  private clearCartHandler() {
    const parentMiniCart = this.closest('mini-cart')! as MiniCart;
    parentMiniCart.bus.emit(CartEvents.CmdClear);

    this.closeConfirm();
  }

  // Add internal logic here as separated functions
  private onCartChanged(e: TypedCartEvent<'cart:changed:debounced'>) {
    const { cart } = e.detail;
    this.render(cart);
  }

  private render(cart: Cart) {
    this.classList.toggle('is-hidden', cart.item_count == 0);
  }

  private bindEvents() {
    this.eventBus!.on(CartEvents.DebouncedChange, this.onCartChanged.bind(this));

    this.clearCartButton?.addEventListener('click', this.openConfirm.bind(this));
    this.clearCartConfirmCancel?.addEventListener('click', this.closeConfirm.bind(this));
    this.clearCartConfirmButton?.addEventListener('click', this.clearCartHandler.bind(this));
  }

  private unbindEvents() {
    this.eventBus!.off(CartEvents.DebouncedChange, this.onCartChanged.bind(this));

    this.clearCartButton?.removeEventListener('click', this.openConfirm.bind(this));
    this.clearCartConfirmCancel?.removeEventListener('click', this.closeConfirm.bind(this));
    this.clearCartConfirmButton?.removeEventListener('click', this.clearCartHandler.bind(this));
  }

  private onMiniCartReady(parentMiniCart: MiniCart) {
    // Make sure we use the same event bus as the parent mini-cart.
    this.eventBus = parentMiniCart.bus;

    this.bindEvents();
  }

  // Lifecycle hooks
  connectedCallback() {
    this.abortController = new AbortController();

    this.clearCartButton = this.querySelector('[data-clear-cart]') as HTMLElement | null;
    this.clearCartConfirmButton = this.querySelector(
      '[data-clear-cart-confirm-button]',
    ) as HTMLElement | null;
    this.clearCartConfirmCancel = this.querySelector(
      '[data-clear-cart-confirm-cancel]',
    ) as HTMLElement | null;

    const parentMiniCart = this.closest('mini-cart')! as MiniCart;
    if (!parentMiniCart) {
      this.logger.error('MiniCartHeader must be a child of a MiniCart element');
      throw new Error('MiniCartHeader must be a child of a MiniCart element');
    }

    parentMiniCart.execOnReady(this.onMiniCartReady.bind(this, parentMiniCart));
  }

  disconnectedCallback() {
    this.abortController?.abort();

    this.unbindEvents();
  }
}

if (!customElements.get('mini-cart-clear-cart')) {
  customElements.define('mini-cart-clear-cart', MiniCartClearCartComponent);
}
